import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { opApi, useOperator } from "@/lib/operator";
import OperatorHeader from "@/components/OperatorHeader";
import { Plus, BookOpen, Tractor, Loader2, ChevronRight, CheckCircle2, Calendar, ClipboardList, PlayCircle, MapPin, Truck, Settings } from "lucide-react";

export default function OperatorHome() {
  const navigate = useNavigate();
  const { operator } = useOperator();
  const isAdmin = !!operator?.is_admin;

  // Spectator accounts (Office TV) and silage-only operators skip the dashboard
  // entirely — straight to the live silage tracker.
  useEffect(() => {
    if (operator?.role === "spectator" || operator?.silage_only) {
      navigate("/app/silage", { replace: true });
    }
  }, [operator, navigate]);
  const [recent, setRecent] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState({ today: 0, week: 0, acres: 0, hours: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      opApi.get("/app/diary?limit=20"),
      opApi.get("/app/jobs").catch(() => ({ data: [] })),
    ])
      .then(([r, jr]) => {
        const entries = r.data || [];
        setRecent(entries.slice(0, 5));
        setJobs(jr.data || []);
        const today = new Date().toISOString().slice(0, 10);
        const weekAgo = new Date(Date.now() - 7 * 86400e3).toISOString().slice(0, 10);
        const todayEntries = entries.filter(e => e.work_date === today);
        const weekEntries  = entries.filter(e => e.work_date >= weekAgo);
        setStats({
          today: todayEntries.length,
          week:  weekEntries.length,
          acres: weekEntries.reduce((a, e) => a + (e.acres || 0), 0),
          hours: weekEntries.reduce((a, e) => a + (e.hours || 0), 0),
        });
      })
      .finally(() => setLoading(false));
  }, []);

  const startJob = async (job) => {
    try { await opApi.post(`/app/jobs/${job.id}/start`); } catch { /* ignore */ }
    // Pre-fill the new-entry wizard with the job's data
    const params = new URLSearchParams({
      from_job: job.id,
      date:     job.planned_date || "",
      machine_vin: job.machine_vin || "",
      machine_label: job.machine_label || "",
      client_id: job.client_id || "",
      client_name: job.client_name || "",
      task: job.task || "",
      field_name: job.field_name || "",
    });
    navigate(`/app/new?${params.toString()}`);
  };

  const openDirections = (job) => {
    if (!job.field_centroid) return;
    const { lat, lon } = job.field_centroid;
    // Universal maps deep link works on iOS + Android + desktop
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <OperatorHeader />

      <div className="px-4 sm:px-6 py-6 max-w-3xl mx-auto space-y-4">
        {/* Today's assigned jobs */}
        {jobs.length > 0 && (
          <div className="bg-gradient-to-br from-[#1a1a5c] to-[#0f0f3a] text-white rounded-2xl p-5 shadow-lg">
            <div className="flex items-center gap-2 mb-3">
              <ClipboardList size={18} className="text-amber-300" />
              <div className="text-[11px] uppercase tracking-widest text-amber-300 font-bold">Your jobs</div>
              <span className="ml-auto bg-[#c41e3a] text-white text-xs font-bold px-2 py-0.5 rounded-full">{jobs.length}</span>
            </div>
            <ul className="space-y-2">
              {jobs.slice(0, 5).map(j => (
                <li key={j.id} className="bg-white/10 rounded-lg p-3" data-testid={`assigned-job-${j.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="text-center shrink-0 w-12">
                      <div className="text-[9px] uppercase tracking-widest text-amber-300/80">{j.planned_date ? new Date(j.planned_date + "T00:00:00").toLocaleDateString("en-GB", { month: "short" }) : ""}</div>
                      <div className="text-xl font-display font-black tabular-nums">{j.planned_date ? new Date(j.planned_date + "T00:00:00").getDate() : "—"}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold">{j.task || "(no task)"}</div>
                      <div className="text-xs text-white/70">
                        {j.client_name || "—"}{j.field_name ? ` · ${j.field_name}` : ""}{j.machine_label ? ` · ${j.machine_label}` : ""}
                      </div>
                      {j.notes && <div className="text-[11px] text-white/60 italic mt-1">{j.notes}</div>}
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      {j.field_centroid && (
                        <button onClick={() => openDirections(j)} data-testid={`directions-${j.id}`}
                                className="p-2 rounded-lg bg-white/10 hover:bg-white/20" title="Directions">
                          <MapPin size={14} />
                        </button>
                      )}
                      <button onClick={() => startJob(j)} data-testid={`start-job-${j.id}`}
                              className="p-2 rounded-lg bg-[#c41e3a] hover:bg-[#d92a47] text-white" title="Start job">
                        <PlayCircle size={14} />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <Link to="/app/new" data-testid="new-entry-btn"
              className="block bg-[#c41e3a] hover:bg-[#d92a47] text-white rounded-2xl p-8 active:scale-[0.98] transition-all shadow-lg">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-xl bg-white/15 grid place-items-center">
              <Plus size={36} strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-display font-black text-2xl">New diary entry</div>
              <div className="text-white/80 text-sm">Log work done today</div>
            </div>
          </div>
        </Link>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Link to="/app/calendar" data-testid="calendar-btn"
                className="bg-white hover:bg-[#1a1a5c] hover:text-white border-2 border-slate-200 hover:border-[#1a1a5c] rounded-2xl p-4 active:scale-95 transition-all group shadow-sm">
            <Calendar size={22} className="text-[#c41e3a] group-hover:text-white mb-2" />
            <div className="font-display font-bold text-base text-[#1a1a5c] group-hover:text-white">Calendar</div>
            <div className="text-[11px] text-slate-500 group-hover:text-white/70 mt-0.5">Pick a day</div>
          </Link>
          <Link to="/app/silage" data-testid="silage-btn"
                className="bg-white hover:bg-[#1a1a5c] hover:text-white border-2 border-slate-200 hover:border-[#1a1a5c] rounded-2xl p-4 active:scale-95 transition-all group shadow-sm relative">
            <Truck size={22} className="text-[#c41e3a] group-hover:text-white mb-2" />
            <div className="font-display font-bold text-base text-[#1a1a5c] group-hover:text-white">Silage Tracker</div>
            <div className="text-[11px] text-slate-500 group-hover:text-white/70 mt-0.5">Live positions</div>
            <span className="absolute top-2 right-2 inline-flex w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          </Link>
          <Link to="/app/diary" data-testid="my-entries-btn"
                className="bg-white hover:bg-[#1a1a5c] hover:text-white border-2 border-slate-200 hover:border-[#1a1a5c] rounded-2xl p-4 active:scale-95 transition-all group shadow-sm">
            <BookOpen size={22} className="text-[#c41e3a] group-hover:text-white mb-2" />
            <div className="font-display font-bold text-base text-[#1a1a5c] group-hover:text-white">My entries</div>
            <div className="text-[11px] text-slate-500 group-hover:text-white/70 mt-0.5">List view</div>
          </Link>
          <Link to="/app/machines" data-testid="machines-btn"
                className="bg-white hover:bg-[#1a1a5c] hover:text-white border-2 border-slate-200 hover:border-[#1a1a5c] rounded-2xl p-4 active:scale-95 transition-all group shadow-sm">
            <Tractor size={22} className="text-[#c41e3a] group-hover:text-white mb-2" />
            <div className="font-display font-bold text-base text-[#1a1a5c] group-hover:text-white">Machines</div>
            <div className="text-[11px] text-slate-500 group-hover:text-white/70 mt-0.5">Fleet list</div>
          </Link>
        </div>

        <div className="grid grid-cols-4 gap-2 bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          {[
            { l: "Today",  v: stats.today },
            { l: "Week",   v: stats.week },
            { l: "Acres",  v: stats.acres.toFixed(1) },
            { l: "Hours",  v: stats.hours.toFixed(1) },
          ].map(s => (
            <div key={s.l} className="text-center">
              <div className="font-display font-black text-2xl text-[#c41e3a] tabular-nums">{s.v}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">{s.l}</div>
            </div>
          ))}
        </div>

        {/* Admin tile — only Andy / Steven see this */}
        {isAdmin && (
          <Link to="/app/admin" data-testid="admin-tile"
                className="block bg-gradient-to-br from-[#1a1a5c] to-[#11113d] text-white rounded-2xl p-5 shadow-lg active:scale-[0.98] transition-all">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-400 text-[#1a1a5c] grid place-items-center shrink-0">
                <Settings size={22} strokeWidth={2.5} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.25em] text-amber-300 font-bold">Admin</div>
                <div className="font-display font-black text-lg">Set up customers, fields, operators…</div>
                <div className="text-xs text-white/70 mt-0.5">Visible only to admins (Andy &amp; Steven)</div>
              </div>
              <ChevronRight size={20} className="text-white/40 shrink-0" />
            </div>
          </Link>
        )}

        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#1a1a5c] font-bold mb-3">Recent entries</div>
          {loading ? (
            <div className="text-slate-500 text-sm flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading…</div>
          ) : recent.length === 0 ? (
            <div className="bg-white border-2 border-dashed border-slate-200 rounded-xl p-8 text-center">
              <div className="text-slate-500 text-sm">No entries yet — tap "New diary entry" to start.</div>
            </div>
          ) : (
            <ul className="space-y-2">
              {recent.map(e => (
                <li key={e.id}>
                  <Link to={`/app/diary/${e.id}`} data-testid={`entry-${e.id}`}
                        className="flex items-center gap-3 bg-white hover:bg-amber-50 border border-slate-200 rounded-xl px-4 py-3 active:scale-[0.99] transition-all">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[#1a1a5c] font-bold truncate">{e.client_name || "—"}</span>
                        {e.status === "invoiced" && <CheckCircle2 size={14} className="text-emerald-500 shrink-0" />}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {e.work_date} · {e.task || "—"} · {e.field_name || "—"}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[#c41e3a] font-bold tabular-nums">{e.acres ? `${e.acres} ac` : "—"}</div>
                      <div className="text-[10px] text-slate-400">{e.hours ? `${e.hours}h` : ""}</div>
                    </div>
                    <ChevronRight size={16} className="text-slate-400 shrink-0" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
